import { formatAsPercentage } from "creso-core/utils";
import {
  PortfolioReview,
  PortfolioReviewReport,
  ReportTopHolding,
  ReviewExposureSnapshot,
} from "creso-core/types";
import { BeforeAfter, RankedList, ReportPageTitle } from "./report-blocks";
import {
  ReportPosition,
  positionsAfter,
  positionsBefore,
  topAmcs,
  topFunds,
} from "./report-data";

const formatPct = (value: number | string) => formatAsPercentage(value, "-", 0);

export function AmcFundExposurePage({
  review,
  report,
  amcName,
}: {
  review: PortfolioReview;
  report: PortfolioReviewReport;
  amcName: (code: string | null) => string;
}) {
  const { before, after } = report.summary.exposure;
  return (
    <div className="flex flex-col gap-6">
      <ReportPageTitle title="AMC and Fund Exposure" />
      <BeforeAfter
        before={
          <ExposureColumn
            snapshot={before}
            positions={positionsBefore(review.lines)}
            amcName={amcName}
          />
        }
        after={
          <ExposureColumn
            snapshot={after}
            positions={positionsAfter(review.lines)}
            amcName={amcName}
          />
        }
      />
    </div>
  );
}

function ExposureColumn({
  snapshot,
  positions,
  amcName,
}: {
  snapshot: ReviewExposureSnapshot;
  positions: ReportPosition[];
  amcName: (code: string | null) => string;
}) {
  const funds = topFunds(positions);
  const fundsPct = funds.reduce((sum, f) => sum + f.pct, 0);
  return (
    <div className="flex flex-col gap-4 border-t border-border pt-4">
      <span className="rounded-lg bg-card px-4 py-3 text-sm font-semibold">
        {snapshot.fund_count} funds from {snapshot.amc_count} AMCs
      </span>
      <div className="flex flex-col gap-4 rounded-lg border border-border p-3">
        <span className="text-sm font-semibold">
          Top 3 AMCs: {formatPct(snapshot.top_3_amc_pct)}
        </span>
        <RankedList items={topAmcs(positions, amcName)} withLogo />
      </div>
      <div className="flex flex-col gap-4 rounded-lg border border-border p-3">
        <span className="text-sm font-semibold">
          Top 3 Funds: {formatPct(fundsPct)}
        </span>
        <RankedList items={funds} withLogo />
      </div>
    </div>
  );
}

export function HoldingsExposurePage({
  title,
  holdings,
}: {
  title: string;
  holdings: { before: ReportTopHolding[]; after: ReportTopHolding[] };
}) {
  const toItems = (list: ReportTopHolding[]) =>
    list.map((h) => ({ key: h.name, name: h.name, pct: Number(h.pct) }));
  return (
    <div className="flex flex-col gap-6">
      <ReportPageTitle title={title} />
      <BeforeAfter
        before={
          <div className="rounded-lg border border-border p-4">
            <RankedList items={toItems(holdings.before)} />
          </div>
        }
        after={
          <div className="rounded-lg border border-border p-4">
            <RankedList items={toItems(holdings.after)} />
          </div>
        }
      />
    </div>
  );
}
